import { Grid, Tooltip, Typography } from '@material-ui/core';
import { Schedule } from '@material-ui/icons';
import {
  Color,
  ColorDynamic,
  Inline,
  OverflowText,
  Stack,
} from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { formatPostingAddress } from 'shared/modules/loadboard/LoadboardUtils';
import { BusinessTypeTag } from 'shared/ui/BusinessTypeTag';
import { formatBusinessHours } from 'shared/utils/businessHoursDisplay';
import styled from 'styled-components';
import {
  formatPostingDistance,
  PostingDistanceDTO,
} from '../../data/dto/PostingDTO';
import { PostingLoadDTO } from '../../data/dto/PostingLoadDTO';

const GutterRoot = styled.div`
  height: 100%;
  padding: 4px 0;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const GutterPoint = styled.div`
  width: 8px;
  height: 8px;
  display: block;
  border-radius: 50%;

  &[data-start='true'] {
    background: ${Color.Yellow300};
  }

  &[data-middle='true'] {
    margin-top: 4px;
    margin-bottom: 4px;
    border: 1px solid ${Color.Dark100};
  }

  &[data-end='true'] {
    border-radius: 12.5%;
    background: ${Color.Green300};
  }
`;

const GutterLine = styled.div`
  flex: 1;
  width: 2px;
  background: repeating-linear-gradient(
    ${ColorDynamic.Silver500},
    ${ColorDynamic.Silver500} 3px,
    transparent 3px,
    transparent 5px
  );
`;

const DeliveryBottomInfo = styled.div`
  padding-left: 16px;
`;

const BusinessHoursIcon = styled(Schedule)`
  margin-top: 2px;
  color: ${ColorDynamic.Dark100};
  font-size: 16px;
`;

function WaypointBusinessHours({ lines }: { lines: string[] }) {
  return (
    <Inline space="xxsmall" verticalAlign="top" noWrap={true}>
      <BusinessHoursIcon titleAccess="Business hours" />
      <Stack space="none">
        {lines.map((line) => (
          <TextBox key={line} color="secondary">
            {line}
          </TextBox>
        ))}
      </Stack>
    </Inline>
  );
}

function Gutter() {
  return (
    <GutterRoot>
      <GutterPoint data-start="true" />
      <GutterLine />
      <GutterPoint data-end="true" />
    </GutterRoot>
  );
}

function GutterMiddle() {
  return (
    <Tooltip title="Along Route Load" placement="left">
      <GutterRoot>
        <GutterLine />
        <GutterPoint data-middle="true" />
        <GutterLine />
      </GutterRoot>
    </Tooltip>
  );
}

interface PostingWaypointsProps {
  load: PostingLoadDTO;
  pickupDistance?: PostingDistanceDTO | null;
  deliveryDistance?: PostingDistanceDTO | null;
  variant?: 'long';
  radius?: { pickup: number | null; delivery: number | null };
}

export function PostingWaypoints({
  load,
  pickupDistance,
  deliveryDistance,
  variant,
  radius,
}: PostingWaypointsProps) {
  const pickupAddress = formatPostingAddress(load.pickup.venue);
  const deliveryAddress = formatPostingAddress(load.delivery.venue);

  const pickupMetroArea =
    load.pickup.venue.metro_area &&
    load.pickup.venue.metro_area.toLowerCase() !==
      load.pickup.venue.city?.toLowerCase()
      ? load.pickup.venue.metro_area
      : null;
  const deliveryMetroArea =
    load.delivery.venue.metro_area &&
    load.delivery.venue.metro_area.toLowerCase() !==
      load.delivery.venue.city?.toLowerCase()
      ? load.delivery.venue.metro_area
      : null;

  const pickupBusinessHours = formatBusinessHours(
    load.pickup.business_hours_type,
    load.pickup.business_hours,
  );
  const deliveryBusinessHours = formatBusinessHours(
    load.delivery.business_hours_type,
    load.delivery.business_hours,
  );

  return (
    <>
      <Grid container={true} spacing={1} wrap="nowrap">
        <Grid item={true}>
          {!load.is_search_along_route ? <Gutter /> : <GutterMiddle />}
        </Grid>

        <Grid item={true} xs={11}>
          <Stack space="xxsmall">
            <Stack space="none">
              <Inline>
                <TextBox
                  noWrap={true}
                  wrapOverflow={true}
                  variant="body-semibold"
                  aria-label="Pickup address"
                >
                  {pickupAddress}
                  {radius?.pickup && (
                    <>
                      {', '}
                      <TextBox color="secondary">
                        {radius.pickup} mi radius
                      </TextBox>
                    </>
                  )}
                </TextBox>

                {variant === 'long' && load.pickup.venue.business_type && (
                  <BusinessTypeTag
                    businessType={load.pickup.venue.business_type}
                  />
                )}
              </Inline>
              <Typography color="textSecondary">
                <OverflowText gutterBottom={false} disableUnderline={true}>
                  {pickupAddress !== pickupDistance?.distance_from &&
                    (formatPostingDistance(pickupAddress, pickupDistance) ||
                      pickupMetroArea)}
                </OverflowText>
              </Typography>

              {variant === 'long' && pickupBusinessHours.length > 0 && (
                <Box marginTop="xxsmall">
                  <WaypointBusinessHours lines={pickupBusinessHours} />
                </Box>
              )}
            </Stack>

            <Inline>
              <TextBox
                noWrap={true}
                wrapOverflow={true}
                variant="body-semibold"
                aria-label="Delivery address"
              >
                {deliveryAddress}
                {radius?.delivery && (
                  <>
                    {', '}
                    <TextBox color="secondary">
                      {radius.delivery} mi radius
                    </TextBox>
                  </>
                )}
              </TextBox>

              {variant === 'long' && load.delivery.venue.business_type && (
                <BusinessTypeTag
                  businessType={load.delivery.venue.business_type}
                />
              )}
            </Inline>
          </Stack>
        </Grid>
      </Grid>

      <DeliveryBottomInfo>
        <Typography color="textSecondary">
          <OverflowText gutterBottom={false} disableUnderline={true}>
            {deliveryAddress !== deliveryDistance?.distance_from &&
              (formatPostingDistance(deliveryAddress, deliveryDistance) ||
                deliveryMetroArea)}
          </OverflowText>
        </Typography>

        {variant === 'long' && deliveryBusinessHours.length > 0 && (
          <Box marginTop="xxsmall">
            <WaypointBusinessHours lines={deliveryBusinessHours} />
          </Box>
        )}
      </DeliveryBottomInfo>
    </>
  );
}
